<script setup>
import { useToast } from '@/core/composables/useToast'

const { toasts, removeToast } = useToast()

function getIcon(type) {
  switch (type) {
    case 'success':
      return 'check_circle'
    case 'error':
      return 'error'
    case 'warning':
      return 'warning'
    case 'info':
    default:
      return 'info'
  }
}
</script>

<template>
  <Teleport to="body">
    <div class="toast-container">
      <TransitionGroup name="toast">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          class="toast"
          :class="`toast-${toast.type}`"
          @click="removeToast(toast.id)"
        >
          <span class="toast-icon material-symbols-rounded">{{ getIcon(toast.type) }}</span>
          <span class="toast-message">{{ toast.message }}</span>
          <button class="toast-close" @click.stop="removeToast(toast.id)">
            <span class="material-symbols-rounded">close</span>
          </button>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<style scoped>
.toast-container {
  position: fixed;
  top: var(--spacing-lg);
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  pointer-events: none;
  max-width: 90vw;
}

.toast {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--border-radius);
  background: var(--bg-elevated);
  box-shadow: var(--shadow-lg);
  pointer-events: auto;
  cursor: pointer;
  min-width: 280px;
  max-width: 400px;
  border-right: 4px solid;
}

.toast-success {
  border-color: var(--color-success);
  background: var(--color-success-light);
}

.toast-success .toast-icon {
  color: var(--color-success);
}

.toast-error {
  border-color: var(--color-error);
  background: var(--color-error-light);
}

.toast-error .toast-icon {
  color: var(--color-error);
}

.toast-warning {
  border-color: var(--color-warning);
  background: var(--color-warning-light);
}

.toast-warning .toast-icon {
  color: var(--color-warning);
}

.toast-info {
  border-color: var(--color-info);
  background: var(--color-info-light);
}

.toast-info .toast-icon {
  color: var(--color-info);
}

.toast-icon {
  font-size: 20px;
  flex-shrink: 0;
}

.toast-message {
  flex: 1;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
}

.toast-close {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: var(--spacing-xs);
  border-radius: var(--border-radius-sm);
  color: var(--text-muted);
  transition: all var(--transition-fast);
}

.toast-close:hover {
  background: rgba(0, 0, 0, 0.1);
  color: var(--text-primary);
}

.toast-close .material-symbols-rounded {
  font-size: 18px;
}

/* Transitions */
.toast-enter-active,
.toast-leave-active {
  transition: all var(--transition-base);
}

.toast-enter-from {
  opacity: 0;
  transform: translateY(-20px);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100px);
}

.toast-move {
  transition: transform var(--transition-base);
}

@media (max-width: 480px) {
  .toast-container {
    left: var(--spacing-md);
    right: var(--spacing-md);
    transform: none;
  }

  .toast {
    min-width: auto;
    max-width: none;
  }
}
</style>
